import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../components/AppIcon';
import AppImage from '../components/AppImage';
import LanguageSelector from '../components/ui/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';

const NavigationLayout = ({ children }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const currentYear = new Date()?.getFullYear();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { path: '/', label: t('navigation.home'), icon: 'Home' }
  ];

  const isActive = (path) => location?.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo + App Name */}
            <Link
              to="/"
              className="flex items-center gap-3 flex-shrink-0 px-3 py-2 rounded-xl hover:bg-yellow-500 transition-all duration-200 shadow-md hover:shadow-lg bg-white">
              <AppImage
                src="/assets/images/image-1765217026326.png"
                alt="Phangan Taxi logo featuring a pickup truck with surfboard on orange background"
                className="h-12 w-auto object-contain rounded-lg" />
              <span className="text-xl font-semibold text-gray-900">Phangan Taxi</span>
            </Link>

            {/* Center: Desktop Navigation Menu */}
            <div className="hidden md:flex items-center justify-center flex-1 px-8">
              <div className="flex items-center gap-2">
                {navigationItems?.map((item) =>
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive(item?.path) ?
                      'bg-amber-400 text-gray-900' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}>
                    {item?.label}
                  </Link>
                )}
              </div>
            </div>

            {/* Right: Language Selector + Mobile Menu */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <LanguageSelector />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle mobile menu">
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} className="text-gray-900" />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen &&
            <>
              <div
                className="fixed inset-0 bg-black/50 z-30 md:hidden"
                onClick={() => setIsMobileMenuOpen(false)} />
              <div className="md:hidden absolute left-0 right-0 top-full bg-white border-b border-gray-200 shadow-lg z-40">
                {navigationItems?.map((item) =>
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-6 py-4 text-sm font-medium border-b border-gray-100 last:border-b-0 transition-colors ${
                      isActive(item?.path) ?
                      'text-amber-600 bg-amber-50' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                    <Icon name={item?.icon} size={20} />
                    <span>{item?.label}</span>
                  </Link>
                )}
              </div>
            </>
          }
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm">
                © {currentYear} Phangan Taxi. {t('footer.rights') || 'All rights reserved.'}
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-4 text-sm">
              <a href="tel:+66123456789" className="hover:text-amber-400 transition-colors">
                📞 +66 123 456 789
              </a>
              <a href="mailto:info@phangantaxi.com" className="hover:text-amber-400 transition-colors">
                ✉️ info@phangantaxi.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NavigationLayout;